/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable no-param-reassign */
import * as Cesium from "cesium";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiResponse,
  ReturnNodeDto,
  SeaSurveyListDto,
  useFileApi,
} from "~/api/GeneratedApi";
import { API_BASE_URL } from "~/config";
import {
  addLoadingTileset,
  removeLoadingTileset,
  toggleCesiumPrimitivesChanged,
} from "~/redux/reducers/mapReducer";
import { useAppDispatch } from "~/redux/store/store";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import useAuth from "~/utils/hooks/useAuth";
import { getTilesetHeightOffsetMatrix } from "../functions/getTilesetHeightOffsetMatrix";
import { setTilesetSettings } from "../functions/setTilesetSettings";
import {
  CesiumMeasurementItem,
  Custom3DTileset,
} from "../types/CesiumMapItems";

type UseCesiumTilesetLoaderReturn = {
  tilesetLoading: boolean;
  tileset?: Cesium.Cesium3DTileset | undefined;
  loader: (args: CesiumTilesetLoaderArgs) => Promise<void>;
  fileId: number | undefined;
  resetTileset: () => void;
};

type CesiumTilesetLoaderArgs = {
  file: ReturnNodeDto;
  markerMapId: string;
  viewer: Cesium.Viewer;
  projection: string;
  seaSurvey: SeaSurveyListDto | CesiumMeasurementItem;
};

export function useCesiumTilesetLoader(): UseCesiumTilesetLoaderReturn {
  const { t } = useTranslation("cesium");
  const [tileset, setTileset] = useState<Cesium.Cesium3DTileset>();
  const tilesetRef = useRef<Cesium.Cesium3DTileset>();
  const [tilesetLoading, setTilesetLoading] = useState(false);
  const [fileId, setFileId] = useState<number>();
  const { getCesiumTileset } = useFileApi();
  const handleApiError = useApiErrorHandler();
  const { getToken } = useAuth();
  const dispatch = useAppDispatch();

  const addTileset = useCallback(
    async (
      url: string,
      file: ReturnNodeDto,
      viewer: Cesium.Viewer,
      mapId: string,
      projection: string,
      seaSurvey: SeaSurveyListDto | CesiumMeasurementItem,
    ) => {
      setFileId(file.id);
      try {
        const token = await getToken();
        if (!token) return;
        const resource = new Cesium.Resource({
          url,
          headers: {
            Authorization: `Bearer ${token}`,
          },
          retryCallback: async (res?: Cesium.Resource) => {
            const tkn = await getToken();
            if (!tkn || !res) return false;
            res.headers = {
              Authorization: `Bearer ${tkn}`,
            };
            return true;
          },
          retryAttempts: 1,
        });
        const tsClass = new Cesium.Cesium3DTileset({
          url: resource,
        });
        const ts = tsClass as Custom3DTileset;

        ts.fileId = file.id;
        ts.projection = projection ? `EPSG:${projection}` : undefined;
        ts.layer = "pointcloud";
        ts.seaSurveyId = seaSurvey.id;
        ts.additionalHeightOffset = seaSurvey.additionalHeightOffsetMm;
        ts.comparisonLevelOffset = seaSurvey.comparisonLevelOffsetMm;
        ts.individualValues = false;
        // Read settings for color etc
        setTilesetSettings(file, ts);

        ts.readyPromise.then(
          () => {
            // Hide the entity from the map
            const entity = viewer.entities.getById(mapId);
            if (entity) {
              entity.show = false;
            }
            viewer.scene.primitives.add(ts);
            dispatch(toggleCesiumPrimitivesChanged());
            setTileset(ts);
            tilesetRef.current = ts;
            // Adjust offsets
            ts.modelMatrix = getTilesetHeightOffsetMatrix(
              ts,
              ts.comparisonLevelOffset + ts.additionalHeightOffset,
            );

            setTilesetLoading(false);
            dispatch(removeLoadingTileset(file.id));
          },
          (x) => {
            if (process.env.NODE_ENV === "development") {
              console.log(x);
            }
            viewer.scene.primitives.remove(ts);
            dispatch(toggleCesiumPrimitivesChanged());

            handleApiError({
              message: t("error-loading-pointcloud"),
              name: "Cesium",
            });
            setTileset(undefined);
            dispatch(removeLoadingTileset(file.id));
            setTilesetLoading(false);
          },
        );
      } catch (error) {
        dispatch(removeLoadingTileset(file.id));
        handleApiError(error as Error | ApiResponse);
      }
    },
    [dispatch, getToken, handleApiError, t],
  );

  const loader = useCallback(
    async ({
      file,
      viewer,
      markerMapId,
      projection,
      seaSurvey,
    }: CesiumTilesetLoaderArgs) => {
      try {
        setTilesetLoading(true);
        dispatch(addLoadingTileset(file.id));
        const url = await getCesiumTileset(file.id);
        if (!url) return;
        const finalUrl = `${API_BASE_URL}${url}`;
        addTileset(finalUrl, file, viewer, markerMapId, projection, seaSurvey);
      } catch (error) {
        dispatch(removeLoadingTileset(file.id));
        handleApiError(error as Error | ApiResponse);
      }
    },
    [dispatch, getCesiumTileset, addTileset, handleApiError],
  );

  const resetTileset = useCallback(() => {
    setTileset(undefined);
    tilesetRef.current = undefined;
    setFileId(undefined);
  }, []);

  return {
    tilesetLoading,
    loader,
    tileset,
    fileId,
    resetTileset,
  };
}
